import {
  CurrentStockResponse,
  Product,
  QuantityUnit,
  Recipe,
  RecipeIngredient,
  ShoppingListItem,
} from "utils/grocyApi.ts";
import { UnitConversionService } from "../../services/UnitConversionService.ts";

export interface IngredientFulfillmentStatus {
  ingredientId: number;
  recipeId: number;
  productId: number;
  productName: string;
  requiredAmount: number;
  availableStockAmount: number;
  shortfallAmount: number;
  shoppingListCoverageAmount: number;
  unit: string;
  isAvailableFromStock: boolean;
  isCoveredByShoppingList: boolean;
  isPartiallyCoveredByShoppingList: boolean;
}

export interface RecipeFulfillmentStatus {
  recipeId: number;
  isFullyAvailable: boolean;
  isFullyCoveredByShoppingList: boolean;
  isPartiallyCoveredByShoppingList: boolean;
  missingIngredients: number;
  missingOnShoppingList: number;
  partiallyCoveredIngredients: number;
  totalIngredients: number;
  ingredients: IngredientFulfillmentStatus[];
}

interface BuildRecipeFulfillmentOptions {
  recipes: Recipe[];
  recipeIngredients: RecipeIngredient[];
  products: Product[];
  quantityUnits: QuantityUnit[];
  stock: CurrentStockResponse[];
  shoppingListItems: ShoppingListItem[];
  conversionService: UnitConversionService | null;
}

interface ShoppingListDibsAmounts {
  genericAmounts: Map<number, number>;
  recipeAmounts: Map<number, Map<number, number>>;
}

function getRecipeIdFromShoppingListNote(
  note: string | null | undefined,
): number | null {
  const match = note?.match(/#recipe:(\d+)/);
  return match ? Number(match[1]) : null;
}

function addAmount(
  amounts: Map<number, number>,
  productId: number,
  amount: number,
): void {
  amounts.set(productId, (amounts.get(productId) ?? 0) + amount);
}

function getActiveShoppingListDibsAmounts(
  shoppingListItems: ShoppingListItem[],
): ShoppingListDibsAmounts {
  const genericAmounts = new Map<number, number>();
  const recipeAmounts = new Map<number, Map<number, number>>();

  for (const item of shoppingListItems) {
    if (item.done) continue;

    const recipeId = getRecipeIdFromShoppingListNote(item.note);

    if (recipeId === null) {
      addAmount(genericAmounts, item.product_id, item.amount);
      continue;
    }

    const amountsForRecipe = recipeAmounts.get(recipeId) ??
      new Map<number, number>();
    addAmount(amountsForRecipe, item.product_id, item.amount);
    recipeAmounts.set(recipeId, amountsForRecipe);
  }

  return { genericAmounts, recipeAmounts };
}

function allocateShoppingListCoverage(
  amounts: Map<number, number>,
  productId: number,
  shortfallAmount: number,
): number {
  const remainingAmount = amounts.get(productId) ?? 0;
  const coverageAmount = Math.min(shortfallAmount, remainingAmount);

  if (coverageAmount > 0) {
    amounts.set(productId, remainingAmount - coverageAmount);
  }

  return coverageAmount;
}

function getIngredientBaseStatus(
  ingredient: RecipeIngredient,
  productsById: Map<number, Product>,
  quantityUnitsById: Map<number, QuantityUnit>,
  stockByProductId: Map<number, CurrentStockResponse>,
  conversionService: UnitConversionService | null,
): IngredientFulfillmentStatus | null {
  const product = productsById.get(ingredient.product_id);
  if (!product) return null;

  const stockUnit = quantityUnitsById.get(product.qu_id_stock);
  const availableStockAmount =
    conversionService?.getAggregatedStock(product.id) ??
      stockByProductId.get(product.id)?.amount ??
      0;

  let requiredAmount = ingredient.amount;
  let isAvailableFromStock = availableStockAmount >= requiredAmount;

  if (conversionService) {
    const stockComparison = conversionService.getIngredientStockComparison(
      ingredient,
      product,
    );
    requiredAmount = stockComparison.convertedRequired;
    isAvailableFromStock = stockComparison.isAvailable;
  }

  return {
    ingredientId: ingredient.id,
    recipeId: ingredient.recipe_id,
    productId: ingredient.product_id,
    productName: product.name,
    requiredAmount,
    availableStockAmount,
    shortfallAmount: isAvailableFromStock
      ? 0
      : Math.max(requiredAmount - availableStockAmount, 0),
    shoppingListCoverageAmount: 0,
    unit: stockUnit?.name ?? "units",
    isAvailableFromStock,
    isCoveredByShoppingList: false,
    isPartiallyCoveredByShoppingList: false,
  };
}

export function buildRecipeFulfillmentStatus({
  recipes,
  recipeIngredients,
  products,
  quantityUnits,
  stock,
  shoppingListItems,
  conversionService,
}: BuildRecipeFulfillmentOptions): Map<number, RecipeFulfillmentStatus> {
  const productsById = new Map(
    products.map((product) => [product.id, product]),
  );
  const quantityUnitsById = new Map(
    quantityUnits.map((unit) => [unit.id, unit]),
  );
  const stockByProductId = new Map(
    stock.map((stockItem) => [stockItem.product_id, stockItem]),
  );
  const ingredientsByRecipe = recipeIngredients.reduce((acc, ingredient) => {
    if (!acc[ingredient.recipe_id]) {
      acc[ingredient.recipe_id] = [];
    }
    acc[ingredient.recipe_id].push(ingredient);
    return acc;
  }, {} as Record<number, RecipeIngredient[]>);

  const { genericAmounts, recipeAmounts } = getActiveShoppingListDibsAmounts(
    shoppingListItems,
  );
  const statusByRecipe = new Map<number, RecipeFulfillmentStatus>();

  for (const recipe of recipes) {
    const ingredients = ingredientsByRecipe[recipe.id] ?? [];
    const ingredientStatuses = ingredients
      .map((ingredient) =>
        getIngredientBaseStatus(
          ingredient,
          productsById,
          quantityUnitsById,
          stockByProductId,
          conversionService,
        )
      )
      .filter((item): item is IngredientFulfillmentStatus => item !== null);

    for (const ingredientStatus of ingredientStatuses) {
      if (ingredientStatus.shortfallAmount <= 0) {
        continue;
      }

      const taggedAmounts = recipeAmounts.get(recipe.id);
      const taggedCoverageAmount = taggedAmounts
        ? allocateShoppingListCoverage(
          taggedAmounts,
          ingredientStatus.productId,
          ingredientStatus.shortfallAmount,
        )
        : 0;
      const genericCoverageAmount = allocateShoppingListCoverage(
        genericAmounts,
        ingredientStatus.productId,
        ingredientStatus.shortfallAmount - taggedCoverageAmount,
      );
      const coverageAmount = taggedCoverageAmount + genericCoverageAmount;

      ingredientStatus.shoppingListCoverageAmount = coverageAmount;
      ingredientStatus.isCoveredByShoppingList =
        coverageAmount >= ingredientStatus.shortfallAmount;
      ingredientStatus.isPartiallyCoveredByShoppingList = coverageAmount > 0 &&
        coverageAmount < ingredientStatus.shortfallAmount;
    }

    const missingIngredients = ingredientStatuses.filter((ingredient) =>
      ingredient.shortfallAmount > 0
    );
    const missingOnShoppingList = missingIngredients.filter((ingredient) =>
      ingredient.isCoveredByShoppingList
    )
      .length;
    const partiallyCoveredIngredients =
      missingIngredients.filter((ingredient) =>
        ingredient.isPartiallyCoveredByShoppingList
      ).length;
    const isFullyCoveredByShoppingList = missingIngredients.length > 0 &&
      missingOnShoppingList === missingIngredients.length;

    statusByRecipe.set(recipe.id, {
      recipeId: recipe.id,
      isFullyAvailable: missingIngredients.length === 0,
      isFullyCoveredByShoppingList,
      isPartiallyCoveredByShoppingList: !isFullyCoveredByShoppingList &&
        (partiallyCoveredIngredients > 0 || missingOnShoppingList > 0),
      missingIngredients: missingIngredients.length,
      missingOnShoppingList,
      partiallyCoveredIngredients,
      totalIngredients: ingredientStatuses.length,
      ingredients: ingredientStatuses,
    });
  }

  return statusByRecipe;
}
